import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/tile-roofing";
const NAME = "Tile Roofing";
const TITLE = "Tile Roof Installation & Repair in Phoenix | Durafoam Roofing";
const DESCRIPTION =
  "Concrete and clay tile roofing in Phoenix — new installs, full re-roofs, underlayment replacement, and tile repair. Family-owned since 1989.";

const faqs = [
  {
    q: "How long does a tile roof last in Phoenix?",
    a: "The tile itself often lasts 50+ years — but the underlayment beneath it wears out at 20–25 years. Most 'tile roof replacements' in the Valley are actually full underlayment replacements with the original tile reused.",
  },
  {
    q: "Can damaged tiles be replaced individually?",
    a: "Yes, and we do it constantly. Cracked or slipped tiles are a single-tile repair as long as the underlayment is still sound. We carry common tile profiles for fast matching.",
  },
  {
    q: "Why does my tile roof leak even though no tiles are broken?",
    a: "Tile is a water-shedding surface; the actual waterproofing is the underlayment beneath. If the underlayment has failed, water gets through even with perfect tile. This is the most common reason older Phoenix tile roofs leak.",
  },
  {
    q: "Should I walk on my tile roof?",
    a: "Only if you absolutely have to and only on the lower third of each tile near the headlap. Tile is fragile — most leak calls we get on tile roofs start with someone unfamiliar walking on it. Hire the inspection out.",
  },
  {
    q: "Can you do a tile-to-foam conversion?",
    a: "Yes. Many Phoenix homeowners convert from tile to foam for energy savings and the elimination of underlayment as a failure mode. We'll quote both options if you're undecided.",
  },
];

export const Route = createFileRoute("/tile-roofing")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="tile-roofing"
      eyebrow="Tile Roofing"
      h1="Tile Roof Installation & Repair"
      subhead="Concrete and clay tile is the iconic Phoenix roof — and when it's maintained right, it lasts generations. We install, repair, and underlayment-restore tile across the Valley."
      heroBullets={[
        "New tile installations and full re-roofs",
        "Underlayment replacement with existing tile",
        "Single-tile and section repairs",
        "Concrete and clay profile matching",
      ]}
      whatIs={{
        heading: "How tile roofing actually works",
        body: (
          <>
            <p>
              A common misconception about tile roofs is that the tile itself keeps water out. It
              doesn't — not entirely. Tile is a water-shedding surface that sends most of the rain
              off the roof, but the actual waterproof membrane is the underlayment underneath:
              a felt or synthetic sheet that catches whatever blows past the tile.
            </p>
            <p>
              That's why tile roof maintenance is mostly about the underlayment, not the tile. When
              someone says "I need a new tile roof," what they usually need is an underlayment
              replacement with the original tile lifted, set aside, and re-laid. Done well, that's
              a 25-year reset and the tile keeps going indefinitely.
            </p>
            <p>
              We work with both concrete and clay tile. Concrete is what most Phoenix tract-builders
              use; clay shows up on higher-end homes and historic restorations. The installation
              practices differ in detail but the underlying logic is the same. If you're budgeting
              a project, you can{" "}
              <Link
                to="/tile-roof-replacement-cost-in-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                see Phoenix tile roof replacement (R&amp;R) cost ranges
              </Link>{" "}
              before we come out. Cost the blocker? <Link to="/financing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Ask us about payment options</Link>.
            </p>

          </>
        ),
      }}
      climate={{
        heading: "Tile roofs and the Phoenix climate",
        body: (
          <>
            <p>
              Tile is a great match for Phoenix in some ways and a tough one in others. Knowing
              both sides helps homeowners make smart maintenance calls.
            </p>
            <ul>
              <li>
                <strong>UV-proof surface:</strong> Tile itself doesn't degrade in the sun. Once
                you've owned a tile roof, you never have to think about the shingle aging cycle.
              </li>
              <li>
                <strong>Underlayment is the weak link:</strong> Standard felt underlayment in
                Phoenix attics often fails at 20–25 years from sustained heat. Premium synthetic
                underlayments can stretch that to 30+.
              </li>
              <li>
                <strong>Monsoon vulnerability:</strong> Wind-driven rain blows under tile and
                relies on the underlayment to catch it. Damaged or cracked tile dramatically
                increases the load on the underlayment.
              </li>
              <li>
                <strong>Foot traffic risk:</strong> Tile cracks easily under improper foot
                placement. Always insist on tile-trained techs for HVAC, satellite, or solar work.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our tile roofing process",
        steps: [
          {
            title: "Inspection and scope",
            body: "We assess tile condition, underlayment age, flashings, and penetrations. You get photos and a written scope — repair, underlayment replacement, or full re-roof.",
          },
          {
            title: "Tile removal and salvage",
            body: "For underlayment work, tiles are carefully removed and stacked for reuse. We typically lose only a small percentage to breakage.",
          },
          {
            title: "Underlayment and flashing",
            body: "Fresh synthetic underlayment, new pipe boots, refreshed wall flashings, and corrected counterflashing where needed.",
          },
          {
            title: "Reset and replace",
            body: "Tiles are reset to spec; replacement tiles match the existing profile and color as closely as possible. Final walk shows you what we did.",
          },
        ],
      }}
      extraSection={{
        heading: "Tile roof work across the Valley",
        body: (
          <>
            <p>
              Concrete and clay tile dominate the Valley's residential roofscape, and we underlayment-replace
              and reset tile in the neighborhoods where it's most common:{" "}
              <Link to="/chandler-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Chandler and Ocotillo</Link>,{" "}
              <Link to="/gilbert-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Gilbert's Val Vista and Power Ranch tracts</Link>,{" "}
              <Link to="/queen-creek-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Queen Creek</Link>, and higher-end tile roofs across{" "}
              <Link to="/scottsdale-roofing-contractor" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Scottsdale's Troon and DC Ranch</Link>{" "}
              and{" "}
              <Link to="/paradise-valley-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Paradise Valley</Link>. If you're weighing a full replacement versus underlayment-only,
              we'll show you both numbers.
            </p>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Tile roof needs eyes on it?"
      ctaBody="Whether it's a single slipped tile or a 25-year-old underlayment, schedule a free inspection and we'll lay out your options."
    />
  );
}
